import React from 'react';
import { Modal, Button, Spin } from 'antd';
import type { ModalProps } from 'antd';
import { X } from '@phosphor-icons/react';
import './oneButton-css/DashboardModal.css';

type DashboardModalProps = {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    children: React.ReactNode;
    theme?: 'dark' | 'light';
    width?: number | string;
    zIndex?: number;
    rootClassName?: string;
    className?: string;
    footer?: React.ReactNode;
    centered?: boolean;
    maskClosable?: boolean;
    destroyOnClose?: boolean;
    getContainer?: false | HTMLElement | (() => HTMLElement);
    loading?: boolean;
    loadingMessage?: React.ReactNode;
    /** Merged into Ant Design `Modal` `styles`; shell always keeps `body.padding: 0`. */
    modalStyles?: ModalProps['styles'];
    /** Rendered in the gradient header between title and close (e.g. Utility popup actions). */
    headerActions?: React.ReactNode;
};

const DashboardModal: React.FC<DashboardModalProps> = ({
    open,
    onClose,
    title,
    children,
    theme = 'dark',
    width = 720,
    zIndex = 12000,
    rootClassName,
    className,
    footer,
    centered = true,
    maskClosable = true,
    destroyOnClose = true,
    getContainer,
    loading = false,
    loadingMessage,
    modalStyles,
    headerActions,
}) => {
    const mergedModalStyles: ModalProps['styles'] = {
        ...modalStyles,
        body: { padding: 0, ...(modalStyles?.body ?? {}) },
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={width}
            centered={centered}
            destroyOnClose={destroyOnClose}
            closable={false}
            maskClosable={maskClosable}
            zIndex={zIndex}
            getContainer={getContainer}
            className={`obd-dashboard-modal obd-unified-modal ${className ?? ''}`.trim()}
            rootClassName={`obd-dashboard-modal-root obd-unified-modal-root obd-theme-${theme} ${rootClassName ?? ''}`.trim()}
            styles={mergedModalStyles}
            title={null}
        >
            <div className="obd-dashboard-modal__shell">
                <header className="obd-dashboard-modal__head">
                    <div className="obd-dashboard-modal__head-inner">
                        <div className="obd-dashboard-modal__title">{title}</div>
                        {headerActions ? (
                            <div className="obd-dashboard-modal__header-actions">{headerActions}</div>
                        ) : null}
                    </div>
                    <button type="button" className="obd-dashboard-modal__close" onClick={onClose} aria-label="Close">
                        <X size={18} weight="bold" />
                    </button>
                </header>
                <div className="obd-dashboard-modal__content">
                    <section className="obd-dashboard-modal__body">{children}</section>
                    <footer className="obd-dashboard-modal__footer">
                        {footer === undefined ? (
                            <Button onClick={onClose}>
                                Close
                            </Button>
                        ) : footer}
                    </footer>
                    {loading && (
                        <div className="obd-dashboard-modal__loading-overlay" role="status" aria-live="polite">
                            <div className="obd-dashboard-modal__loading-inner">
                                <Spin size="default" />
                                <span className="obd-dashboard-modal__loading-text">{loadingMessage ?? 'Loading...'}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default DashboardModal;
